import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileText, HelpCircle, Loader2, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import {
  WorkspaceDrawerActionsMenu,
  type WorkspaceDrawerActionItem,
} from "../Workspace/WorkspaceDrawerActionsMenu";
import { WorkspaceDrawerActionRow } from "../Workspace/WorkspaceDrawerActionRow";
import {
  WORKSPACE_SIMULATION_POLICY_PREVIEW_VISIBILITY,
  type WorkspaceSimulationPolicyPreviewVisibilityDetail,
} from "../Workspace/workspaceSimulationPolicyPreviewEvents";
import { DeleteSimulationDialog } from "./DeleteSimulationDialog";
import {
  getComparisonRunSavedTitle,
  MAX_COMPARISON_RUN_TITLE_LENGTH,
  type ComparisonRun,
} from "./simulatorComparisonTypes";

export type SimulationResultsDrawerChromeProps = {
  onClose?: () => void;
  run: ComparisonRun | null;
  loading?: boolean;
  disabled?: boolean;
  onRename: (title: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onExport: () => void;
  onShowPolicy?: () => void;
  /** Save simulation rules as a new strategy policy version. */
  onSaveAsPolicyVersion?: () => Promise<void>;
  canSaveAsPolicyVersion?: boolean;
};

export function SimulationResultsDrawerChrome({
  onClose,
  run,
  loading = false,
  disabled = false,
  onRename,
  onDelete,
  onExport,
  onShowPolicy,
  onSaveAsPolicyVersion,
  canSaveAsPolicyVersion = false,
}: SimulationResultsDrawerChromeProps): React.ReactElement {
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingPolicyVersion, setSavingPolicyVersion] = useState(false);
  const [policyCardMounted, setPolicyCardMounted] = useState(false);

  const savedTitle = useMemo(
    () => (run ? getComparisonRunSavedTitle(run).trim() : ""),
    [run]
  );

  const simulationId = useMemo(
    () => (run?.backtestId || run?.id || "").trim() || null,
    [run]
  );

  const isPolicyVersionSaved = Boolean(run?.isSavedStrategyVersion);
  const hasPersistedSimulation = Boolean(run?.backtestId?.trim() || run?.isSavedBacktest);

  useEffect(() => {
    setPolicyCardMounted(false);
  }, [simulationId]);

  useEffect(() => {
    if (!onShowPolicy || !simulationId) return;
    const onVisibility = (event: Event) => {
      const detail = (event as CustomEvent<WorkspaceSimulationPolicyPreviewVisibilityDetail>)
        .detail;
      if (!detail) return;
      if (detail.open && detail.simulationId && detail.simulationId !== simulationId) return;
      setPolicyCardMounted(Boolean(detail.open && detail.simulationId === simulationId));
    };
    window.addEventListener(WORKSPACE_SIMULATION_POLICY_PREVIEW_VISIBILITY, onVisibility);
    return () =>
      window.removeEventListener(WORKSPACE_SIMULATION_POLICY_PREVIEW_VISIBILITY, onVisibility);
  }, [onShowPolicy, simulationId]);

  useEffect(() => {
    if (!renameOpen) return;
    setRenameDraft(savedTitle);
  }, [renameOpen, savedTitle]);

  const actionsDisabled = disabled || loading || !run;

  const handleRenameConfirm = useCallback(async () => {
    const nextTitle = renameDraft.trim();
    if (!nextTitle || nextTitle === savedTitle) {
      setRenameOpen(false);
      return;
    }
    setRenaming(true);
    try {
      await onRename(nextTitle);
      setRenameOpen(false);
    } finally {
      setRenaming(false);
    }
  }, [onRename, renameDraft, savedTitle]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await onDelete();
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  }, [onDelete]);

  const drawerActionItems = useMemo((): WorkspaceDrawerActionItem[] => {
    if (loading) return [];
    const items: WorkspaceDrawerActionItem[] = [];

    if (onShowPolicy && !policyCardMounted) {
      items.push({
        id: "show-policy",
        label: "Show policy",
        disabled: actionsDisabled,
        icon: <FileText className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: onShowPolicy,
      });
    }

    items.push(
      {
        id: "rename-simulation",
        label: "Rename simulation",
        disabled: actionsDisabled,
        icon: <Pencil className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: () => setRenameOpen(true),
      },
      {
        id: "export-simulation",
        label: "Export",
        disabled: actionsDisabled,
        icon: <Download className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: onExport,
      },
      {
        id: "delete-simulation",
        label: deleting ? "Deleting…" : "Delete",
        disabled: actionsDisabled || deleting,
        tone: "destructive",
        separatorBefore: true,
        icon: <Trash2 className="size-4 shrink-0 text-current" aria-hidden />,
        onSelect: () => setDeleteOpen(true),
      },
      {
        id: "need-help",
        label: "Need Help?",
        href: "https://help.tesseratrades.com/simulator",
        tone: "brand",
        separatorBefore: true,
        icon: <HelpCircle className="size-4 shrink-0 text-current" aria-hidden />,
      }
    );

    return items;
  }, [
    actionsDisabled,
    deleting,
    loading,
    onExport,
    onShowPolicy,
    policyCardMounted,
  ]);
  const handleSaveAsPolicyVersion = useCallback(async () => {
    if (!onSaveAsPolicyVersion || !canSaveAsPolicyVersion || isPolicyVersionSaved) return;
    setSavingPolicyVersion(true);
    try {
      await onSaveAsPolicyVersion();
    } finally {
      setSavingPolicyVersion(false);
    }
  }, [canSaveAsPolicyVersion, isPolicyVersionSaved, onSaveAsPolicyVersion]);

  return (
    <>
      <WorkspaceDrawerActionRow onClose={onClose}>
        <WorkspaceDrawerActionsMenu items={drawerActionItems} />
        {loading ? null : (
          <>
            {onShowPolicy && !policyCardMounted ? (
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                data-simulation-show-policy-button=""
                className="shrink-0"
                aria-label="Show policy"
                disabled={actionsDisabled}
                onClick={onShowPolicy}
              >
                Show policy
              </Button>
            ) : null}
            {onSaveAsPolicyVersion ? (
              <Button
                type="button"
                variant={isPolicyVersionSaved ? "outline-gray" : "gradient"}
                size="sm"
                data-font="primary"
                data-simulation-save-policy-version-button=""
                className="shrink-0"
                aria-label={
                  isPolicyVersionSaved ? "Policy version saved" : "Save as policy version"
                }
                disabled={
                  actionsDisabled ||
                  savingPolicyVersion ||
                  isPolicyVersionSaved ||
                  !canSaveAsPolicyVersion
                }
                title={
                  isPolicyVersionSaved
                    ? "These rules are already saved as a policy version"
                    : !canSaveAsPolicyVersion
                      ? hasPersistedSimulation
                        ? "Link this simulation to a strategy to save a policy version"
                        : "Save the simulation first, then save as a policy version"
                      : "Save these rules as a new policy version on the linked strategy"
                }
                onClick={() => void handleSaveAsPolicyVersion()}
              >
                {savingPolicyVersion ? (
                  <Loader2 data-icon="inline-start" className="size-4 shrink-0 animate-spin" aria-hidden />
                ) : null}
                {isPolicyVersionSaved ? "Policy version saved" : "Save as policy version"}
              </Button>
            ) : null}
          </>
        )}
      </WorkspaceDrawerActionRow>

      <StrategyPineDetailDialog
        open={renameOpen}
        onOpenChange={(open) => {
          if (!open && renaming) return;
          setRenameOpen(open);
        }}
        title="Rename simulation"
        showDismissButton={false}
        contentClassName="sm:max-w-md"
        footer={
          <div
            data-dashboard-layout-dialog-footer=""
            className="inline-flex max-w-full flex-wrap items-center justify-end gap-2"
          >
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={renaming}
              onClick={() => setRenameOpen(false)}
            >
              <span className="normal-case tracking-normal">Cancel</span>
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={renaming || !renameDraft.trim()}
              aria-busy={renaming}
              onClick={() => void handleRenameConfirm()}
            >
              <span className="normal-case tracking-normal">
                {renaming ? "Saving…" : "Save"}
              </span>
            </Button>
          </div>
        }
      >
        <Input
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value.slice(0, MAX_COMPARISON_RUN_TITLE_LENGTH))}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleRenameConfirm();
            if (e.key === "Escape" && !renaming) setRenameOpen(false);
          }}
          maxLength={MAX_COMPARISON_RUN_TITLE_LENGTH}
          aria-label="Simulation title"
          placeholder="Simulation title"
          disabled={renaming}
          className="h-11 font-[family-name:var(--font-primary)] text-sm normal-case tracking-normal"
        />
      </StrategyPineDetailDialog>

      <DeleteSimulationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        isDeleting={deleting}
        onConfirm={() => void handleDeleteConfirm()}
      />
    </>
  );
}
